"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Box, Cpu, ShieldAlert, Wind, Wrench } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const inclusions = [{
  index: "01",
  code: "MEC-BASE",
  title: "Bull & Motion Base",
  description: "The primary mechanical assembly where included with the product.",
  icon: Box
}, {
  index: "02",
  code: "CTRL-SYS",
  title: "Operator Controller",
  description: "Confirm whether the controller is included with the exact machine.",
  icon: Cpu
}, {
  index: "03",
  code: "ARN-INFL",
  title: "Inflatable Safety Arena",
  description: "Some packages include an inflatable safety area, while others may not.",
  icon: ShieldAlert
}, {
  index: "04",
  code: "AIR-BLWR",
  title: "Blower",
  description: "Check whether the appropriate blower is part of the package.",
  icon: Wind
}, {
  index: "05",
  code: "CAB-ACCS",
  title: "Cables & Accessories",
  description: "Confirm which cables, covers, remotes or other accessories are included.",
  icon: Wrench
}];
export default function Inclusions() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.06
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 12
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    }
  };
  return <section id="shipmentinclusions" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 md:px-6 max-w-6xl">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 font-mono text-xs font-semibold tracking-widest uppercase text-primary mb-3">
            <span className="inline-block w-2 h-2 bg-primary"></span>
            <span>Packaging & Verification</span>
          </div>

          <h2 className="font-serif text-3xl md:text-5xl font-bold tracking-tight uppercase mb-4 text-foreground">
            What May Be Included in a Mechanical Bull Shipment?
          </h2>

          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
            The contents of a shipment depend on the individual product and package.
            Do not assume every machine includes the same equipment.
          </p>
        </div>

        {/* 5-Card Industrial Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {inclusions.map((item, i) => {
          const IconComponent = item.icon;
          return <motion.div key={item.code} data-index={i} variants={cardVariants} className="bg-card text-card-foreground border border-border p-6 md:p-8 flex flex-col justify-between shadow-xs transition-colors hover:border-primary/50">
                <div>
                  <div className="flex items-center justify-between border-b border-border pb-4 mb-5">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs text-primary font-bold">
                        {item.index}
                      </span>
                      <span className="font-mono text-xs text-muted-foreground tracking-wider uppercase">
                        [{item.code}]
                      </span>
                    </div>
                    <div className="text-muted-foreground">
                      <IconComponent className="size-5" />
                    </div>
                  </div>

                  <h3 className="font-serif text-xl md:text-2xl font-bold uppercase tracking-wide mb-3 text-card-foreground">
                    {item.title}
                  </h3>

                  <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                    {item.description}
                  </p>
                </div>

                <div className="pt-6 mt-6 border-t border-border/60 flex items-center justify-between text-xs font-mono text-muted-foreground">
                  <span>SPECIFICATION</span>
                  <span className="text-foreground uppercase font-medium">Verify Item</span>
                </div>
              </motion.div>;
        })}
        </motion.div>

        {/* Disclaimer & Link Utility Footer */}
        <div className="mt-12 pt-8 border-t border-border flex flex-col md:flex-row items-start md:items-center justify-between gap-6 bg-muted/30 p-6">
          <div className="max-w-2xl">
            <p className="text-sm md:text-base text-foreground font-medium">
              The individual product listing and order confirmation should identify what is actually being supplied.
            </p>
          </div>

          <div className="shrink-0">
            <Button asChild variant="outline" className="border-border hover:border-foreground">
              <Link to="/MechanicalBullBuyingGuidePage" className="inline-flex items-center gap-2">
                <span>View Buying Guide</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}